import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLicense } from "../../context/licenseContext";
import apiClient from "../../api/apiClient";
import "./Menu.css";

import InventoryIcon from "../Icons/inventoyrIcon";
import SettingsIcon from "../Icons/settingsIcon";
import ShowroomIcon from "../Icons/showroomIcon";
import AdminIcon from "../Icons/adminIcon";
import CRMIcon from "../Icons/crmIcon";
import DocumentsIcon from "../Icons/documentsIcon";
import SalesAnalyticsIcon from "../Icons/salesAnalyticsIcon";
import OrdersIcon from "../Icons/ordersIcon";

const MENU_STORAGE_KEY = "dyc_menu_expanded";
 
const Menu = ({ userId, userProfile, headerTitle }) => {
    const location = useLocation(); 
    const [user, setUser] = useState({});
    const [title, setTitle] = useState("");
    const { currentLicense } = useLicense();
    const navigate = useNavigate();
    const [myPermissions, setMyPermissions] = useState(() => {
        try {
            const saved = localStorage.getItem("currentPermissions");
            return saved ? JSON.parse(saved) : null;
        } catch {
            return null;
        }
    });
    const [orderActionCount, setOrderActionCount] = useState(0);
    const [crmFlyoutOpen, setCrmFlyoutOpen] = useState(false);
    const [flyoutTop, setFlyoutTop] = useState(0);
    const crmItemRef  = useRef(null);
    const crmFlyoutRef = useRef(null);

    // Initialize from localStorage — falls back to false if no value stored
    const [isExpanded, setIsExpanded] = useState(() => {
        try {
            const stored = localStorage.getItem(MENU_STORAGE_KEY);
            return stored !== null ? JSON.parse(stored) : false;
        } catch {
            return false;
        }
    });

    // Sync CSS variable on mount so layout matches persisted state immediately
    useEffect(() => {
        document.documentElement.style.setProperty(
            "--sidebar-current-width",
            isExpanded ? "var(--sidebar-width)" : "var(--sidebar-collapsed)"
        );
    }, []); // intentionally runs once on mount

    // Persist state and sync CSS variable on every toggle
    useEffect(() => {
        try {
            localStorage.setItem(MENU_STORAGE_KEY, JSON.stringify(isExpanded));
        } catch {
            // localStorage unavailable (e.g. private browsing with storage blocked) — fail silently
        }
        document.documentElement.style.setProperty(
            "--sidebar-current-width",
            isExpanded ? "var(--sidebar-width)" : "var(--sidebar-collapsed)"
        );
    }, [isExpanded]);

    useEffect(() => {
        const fetchUserInfo = async () => {
            try {
                setTitle(currentLicense?.title || "");
                console.log("Current License:", currentLicense);
            } catch (error) {
                console.error("Error fetching user info:", error);
            } 
        };
        fetchUserInfo();
    }, [userId, currentLicense]);

    useEffect(() => {
        if (!currentLicense?.licenseNo) return;
        apiClient
            .get(`/permissions/my-permissions?licenseNo=${currentLicense.licenseNo}`)
            .then((res) => {
                setMyPermissions(res.data);
                localStorage.setItem("currentPermissions", JSON.stringify(res.data));
            })
            .catch(() =>
                setMyPermissions({
                    can_upload_coa: 1,
                    can_view_showroom: 1,
                    can_see_coa_options: 1,
                    can_view_lab_tests: 1,
                    can_order_tests: 1,
                })
            );
    }, [currentLicense]);

    useEffect(() => {
        if (!currentLicense?.licenseNo || myPermissions?.can_manage_orders !== 1) return;
        apiClient
            .get(`/orders?licenseNo=${currentLicense.licenseNo}&status=all&needs_action=true&type=marketplace&role=all&page=1&limit=1`)
            .then((res) => setOrderActionCount(res.data?.total || 0))
            .catch(() => setOrderActionCount(0));
    }, [currentLicense?.licenseNo, myPermissions?.can_manage_orders]);

    const toggleMenu = () => {
        setIsExpanded((prev) => !prev);
    };

    // Close flyout when clicking outside
    useEffect(() => {
        if (!crmFlyoutOpen) return;
        const handle = (e) => {
            if (
                crmFlyoutRef.current && !crmFlyoutRef.current.contains(e.target) &&
                crmItemRef.current  && !crmItemRef.current.contains(e.target)
            ) {
                setCrmFlyoutOpen(false);
            }
        };
        document.addEventListener("mousedown", handle);
        return () => document.removeEventListener("mousedown", handle);
    }, [crmFlyoutOpen]);

    // Close flyout on route change away from CRM
    useEffect(() => {
        if (!location.pathname.startsWith("/CRMPanel")) setCrmFlyoutOpen(false);
    }, [location.pathname]);

    const handleCrmClick = useCallback(() => {
        if (crmItemRef.current) {
            const rect = crmItemRef.current.getBoundingClientRect();
            // Anchor flyout top to the CRM item's top, clamped so it never goes off-screen
            const flyoutHeight = 200; // approximate
            const maxTop = window.innerHeight - flyoutHeight - 12;
            setFlyoutTop(Math.min(rect.top, maxTop));
        }
        setCrmFlyoutOpen(prev => !prev);
    }, []);

    const activeStyle = {
        filter: "brightness(0) saturate(100%) invert(35%) sepia(64%) saturate(2092%) hue-rotate(322deg) brightness(97%) contrast(87%)",
    };

    return (
        <>
            <div className={`menu ${isExpanded ? "expanded" : "collapsed"}`} data-tutorial="nav-menu">
                <div className="top-elements">
                    <div className="menu-icons">
                        <div className="menu-item" onClick={toggleMenu}>
                            <div className="menu-icon">
                                <div className="hamburger-icon">
                                    <div></div>
                                    <div></div>
                                    <div></div>
                                </div>
                            </div>
                            {isExpanded && <span className="menu-text">Menu</span>}
                        </div>
                        <div
                            onClick={() => navigate("/Dashboard")}
                            className={`menu-item ${location.pathname === "/Dashboard" ? "active-icon" : ""}`}
                            data-tutorial="nav-inventory"
                        >
                            <div className="menu-icon"><InventoryIcon /></div>
                            {isExpanded && <span className="menu-text">Inventory</span>}
                        </div> 
                        {(title.toLowerCase() === "owner" ||
                            title.toLowerCase() === "manager" ||
                            title.toLowerCase() === "buyer" ||
                            title.toLowerCase() === "admin") &&
                            myPermissions?.can_view_showroom !== 0 && (
                                <div 
                                    onClick={() => navigate("/showroom")}
                                    className={`menu-item ${location.pathname === "/showroom" ? "active-icon" : ""}`}
                                    data-tutorial="nav-showroom"
                                >
                                    <div className="menu-icon"><ShowroomIcon /></div>
                                    {isExpanded && <span className="menu-text">DYC Marketplace</span>}
                                </div>
                            )}
                        {myPermissions?.can_manage_orders === 1 && (
                            <div
                                onClick={() => navigate("/orders")}
                                className={`menu-item ${location.pathname === "/orders" ? "active-icon" : ""}`}
                                data-tutorial="nav-orders"
                            >
                                <div className="menu-icon"><OrdersIcon /></div>
                                {isExpanded && ( 
                                    <span className="menu-text">
                                        Wholesale Orders
                                        {orderActionCount > 0 && (
                                            <span className="menu-badge">{orderActionCount}</span>
                                        )}
                                    </span>
                                )} 
                                {!isExpanded && orderActionCount > 0 && (
                                    <span className="menu-badge-dot" />
                                )}
                            </div>
                        )}
                        {myPermissions?.can_view_compliance_docs === 1 && (
                            <div
                                onClick={() => navigate("/compliance-documents")}
                                className={`menu-item ${location.pathname === "/compliance-documents" ? "active-icon" : ""}`}
                                data-tutorial="nav-documents"
                            >
                                <div className="menu-icon"><DocumentsIcon /></div>
                                {isExpanded && <span className="menu-text">Documents</span>}
                            </div>
                        )}
                        {(myPermissions?.can_view_lab_tests !== 0 || myPermissions?.can_order_tests !== 0) && (
                            <div
                                onClick={() => navigate("/LabOrders")}
                                className={`menu-item ${location.pathname === "/LabOrders" ? "active-icon" : ""}`}
                                data-tutorial="nav-lab-orders"
                            >
                                <div className="menu-icon">
                                    <svg
                                        fill="var(--icons)"
                                        viewBox="0 0 32 32"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path d="M20 2h-8a1 1 0 0 0-1 1v10.382L7.105 21.553A3 3 0 0 0 9.789 26h12.422a3 3 0 0 0 2.684-4.447L21 13.382V3a1 1 0 0 0-1-1zm-7 1h6v2h-6V3zm6 10.382V17h-6v-3.618L11 9.382V7h10v2.382L19 13.382zM22.895 22.553A1 1 0 0 1 22.211 24H9.789a1 1 0 0 1-.894-1.447L11 19h10l2.105 3.107-.21.446z" />
                                        <circle cx="13" cy="21" r="1" />
                                        <circle cx="17" cy="22" r="1" />
                                        <circle cx="15" cy="20" r="1" />
                                    </svg>
                                </div>
                                {isExpanded && <span className="menu-text">Lab Orders</span>}
                            </div>
                        )}
                        {/* Sales Analytics — disabled until feature is complete
                        <div
                            onClick={() => navigate("/SalesAnalytics")}
                            className={`menu-item ${location.pathname.startsWith("/SalesAnalytics") ? "active-icon" : ""}`}
                        >
                            <SalesAnalyticsIcon />
                            {isExpanded && <span className="menu-text">Sales Analytics</span>}
                        </div>
                        */}
                    </div>
                </div>
                <div className="bottom-elements">
                    {(userProfile === "admin" || userProfile === "superadmin") && (
                        <div
                            onClick={() => navigate("/CRMPanel/outbound")}
                            className={`menu-item ${location.pathname.startsWith("/CRMPanel") ? "active-icon" : ""}`}
                        >
                            <CRMIcon />
                            {isExpanded && <span className="menu-text">CRM Panel</span>}
                        </div>
                    )}
                    {(userProfile === "admin" || userProfile === "superadmin") && (
                        <div
                            onClick={() => navigate("/AdminPanel")}
                            className={`menu-item ${location.pathname === "/AdminPanel" ? "active-icon" : ""}`}
                        >
                            <AdminIcon />
                            {isExpanded && <span className="menu-text">Admin Panel</span>}
                        </div>
                    )}
                            <div
                                onClick={() => navigate("/settings")}
                                className={`menu-item ${location.pathname.startsWith("/settings") ? "active-icon" : ""}`}
                                data-tutorial="nav-settings"
                            >
                                <div className="menu-icon"><SettingsIcon /></div>
                                {isExpanded && <span className="menu-text">Settings</span>}
                            </div>
                </div>
            </div>
            {/* ── CRM Panel flyout ──────────────────────────────────────── */}
            {crmFlyoutOpen && (userProfile === "admin" || userProfile === "superadmin") && (
                <>
                    <div className="crm-flyout-backdrop" onClick={() => setCrmFlyoutOpen(false)} />
                    <div
                        className="crm-flyout"
                        ref={crmFlyoutRef}
                        style={{ top: flyoutTop }}
                    >
                        <p className="crm-flyout__heading">CRM Panel</p>

                        {/* Inbound */}
                        <div
                            className={`crm-flyout__card${location.pathname === "/CRMPanel/inbound" ? " crm-flyout__card--active" : ""}`}
                            onClick={() => { navigate("/CRMPanel/inbound"); setCrmFlyoutOpen(false); }}
                        >
                            <div className="crm-flyout__card-icon">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M22 12h-6l-2 3H10l-2-3H2"/>
                                    <path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z"/>
                                </svg>
                            </div>
                            <div className="crm-flyout__card-body">
                                <span className="crm-flyout__card-title">Inbound</span>
                                <span className="crm-flyout__card-sub">Lead capture &amp; Newsletter Marketing</span>
                            </div>
                            <svg className="crm-flyout__card-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="9 18 15 12 9 6"/>
                            </svg>
                        </div>
                                     
                        {/* Outbound */}
                        <div
                            className={`crm-flyout__card${location.pathname === "/CRMPanel/outbound" ? " crm-flyout__card--active" : ""}`}
                            onClick={() => { navigate("/CRMPanel/outbound"); setCrmFlyoutOpen(false); }}
                        >
                            <div className="crm-flyout__card-icon">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="22 17 13.5 8.5 8.5 13.5 2 7"/>
                                    <polyline points="16 17 22 17 22 11"/>
                                </svg>
                            </div>
                            <div className="crm-flyout__card-body">
                                <span className="crm-flyout__card-title">Outbound</span>
                                <span className="crm-flyout__card-sub">Cold Email Campaigns &amp; pipeline</span>
                            </div>
                            <svg className="crm-flyout__card-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="9 18 15 12 9 6"/>
                            </svg>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default Menu;
